import { useSearchParams } from 'react-router-dom'
import { usePromotionStats } from '@/api/promotions'
import { cn } from '@/lib/utils'
import PromotionList from './PromotionList'
import SearchTermAnalysis from './SearchTermAnalysis'
import RulesTabRedirect from './RulesTabRedirect'
import CleanupView from './CleanupView'
import AutoApplyTab from './AutoApplyTab'

type Tab = 'queue' | 'search-terms' | 'rules' | 'auto-apply' | 'cleanup'

const VALID_TABS: ReadonlyArray<Tab> = ['queue', 'search-terms', 'rules', 'auto-apply', 'cleanup']

export function parseTab(value: string | null): Tab {
  return (VALID_TABS as ReadonlyArray<string>).includes(value ?? '') ? (value as Tab) : 'queue'
}

const tabs: Array<{ id: Tab; label: string }> = [
  { id: 'queue', label: 'Queue' },
  { id: 'search-terms', label: 'Search Terms' },
  { id: 'rules', label: 'Regeln' },
  { id: 'auto-apply', label: 'Auto-Apply' },
  { id: 'cleanup', label: 'Cleanup' },
]

export default function Promotions() {
  const [params, setParams] = useSearchParams()
  const activeTab = parseTab(params.get('tab'))
  const { data: statsData } = usePromotionStats()
  const pendingCount = statsData?.stats?.pending ?? 0

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">
          Promotion Queue
          {pendingCount > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              {pendingCount} pending
            </span>
          )}
        </h1>
        <p className="text-sm text-[var(--muted-foreground)]">
          Keyword-Graduation, Search-Term-Analyse und Cleanup
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-[var(--border)]">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              const next = new URLSearchParams(params)
              next.set('tab', tab.id)
              setParams(next, { replace: true })
            }}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
              activeTab === tab.id
                ? 'border-[var(--foreground)] text-[var(--foreground)]'
                : 'border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'queue' && <PromotionList />}
      {activeTab === 'search-terms' && <SearchTermAnalysis />}
      {activeTab === 'rules' && <RulesTabRedirect />}
      {activeTab === 'auto-apply' && <AutoApplyTab />}
      {activeTab === 'cleanup' && <CleanupView />}
    </div>
  )
}
